import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BatchSchema} from 'constants/SampleConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';

import filtersToArgs from './filtersToArgs';
import {
  COMPARABLE_FIELD_TYPES,
  Filter as NewFilter,
  FILTER_PART_SEPARATOR,
  FilterKey,
  FILTERS,
  GroupByKeyFilter,
  isMultiValueFieldType,
  isTextFieldType,
  keyToEmptyFilter,
  Operator,
  SearchParams,
  SingleValueFilter,
  isStringFieldType,
  isMultiValueFilter,
  MultiValueFilter,
  StandardFilterDataType,
  standardOperatorsForKey,
  isNumericFieldType,
} from './SearchConstants';

export {filtersToArgs};

interface DeprecatedFilter {
  key: FilterKey;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}

type Filter = DeprecatedFilter | NewFilter;

/**
 * Replaces (or adds) current filters with new filters
 * @param {array} current Array of key/value filter shapes
 * @param {array} newFilters List of new key/value filters to replace in current
 */
export function replaceByKey<TOrig extends Filter, TNew extends Filter>(
  current: Array<TOrig>,
  ...newFilters: Array<TNew>
): Array<TOrig | TNew> {
  const newFilterKeyToFilters: {[filterKey: string]: Array<TNew>} = {};
  newFilters.forEach((filter) => {
    const key = filter.key;
    const filtersMatchingKey = newFilterKeyToFilters[key];
    if (filtersMatchingKey) {
      filtersMatchingKey.push(filter);
    } else {
      newFilterKeyToFilters[key] = [filter];
    }
  });

  const updatedFilters: Array<TOrig | TNew> = [];
  const currentFilterKeys = new Set<string>();
  current.forEach((currentFilter) => {
    const key = currentFilter.key;
    currentFilterKeys.add(key);
    const matchingFilters = newFilterKeyToFilters[key];
    if (matchingFilters) {
      matchingFilters.forEach((filter) => {
        updatedFilters.push(filter);
      });
    } else {
      updatedFilters.push(currentFilter);
    }
  });

  Object.keys(newFilterKeyToFilters).forEach((key) => {
    if (!currentFilterKeys.has(key)) {
      newFilterKeyToFilters[key].forEach((filter) => {
        updatedFilters.push(filter);
      });
    }
  });

  return updatedFilters;
}

/**
 * Encode a filter value for use in a query string by escaping special chars (e.g., colons).
 *
 * Use percent-encoding (AKA URL-encoding), which is also used to generally encode URL strings. Because URL-
 * encoding converts special chars into % + hex (e.g, %3A), and %-signs can themselves be URL-encoded, we can
 * encode and decode a string repeatedly to preserve multiple "levels" of special chars.
 *
 * @param {String} value
 */
export function encodeValue(value: string | number | null | undefined): string {
  return encodeURIComponent(String(value));
}

export function decodeValue(value: string): string {
  return decodeURIComponent(value);
}

export function createMatchAnyFilter(operator: string, value: string): string {
  return `${operator}${FILTER_PART_SEPARATOR}${value}`;
}

export function parseMatchAnyFilter(filterString: string): {operator: string; value: string} {
  const [operator, value] = filterString.split(FILTER_PART_SEPARATOR);
  return {operator, value};
}

interface TagFilter {
  name: string | undefined;
  op: string | undefined;
  value: string | undefined;
  valueType: string | undefined;
}

/**
 * Parses TAG and BATCH_TAG filters from strings to objects, decoding values.
 *
 * e.g., 'lte:3:tag:  ft_integer:' is returned as {name: tag, value: 3, valueType: ft_integer, op: lte}.
 *
 * If filter is the empty string, each component will be undefined.
 */
export function parseTagFilter(filterKey: FilterKey, filter: string): TagFilter {
  if (!filter) {
    return {
      name: undefined,
      op: undefined,
      value: undefined,
      valueType: undefined,
    };
  }
  const filterParts = filter.split(FILTER_PART_SEPARATOR).map((val) => decodeValue(val));
  const op = filterParts[0];
  const value = filterParts[1];
  const name = filterParts[2];
  const valueType = filterParts[3];
  return {name, op, value, valueType};
}

/**
 * Creates a TAG or BATCH_TAG filter string from a fieldName, value, fieldType, and op.
 * Values are encoded to escape special characters (e.g., the delimiting FILTER_PART_SEPARATOR).
 *
 * String format is: <op>:<value>:<fieldName>:<fieldType>
 */
export function createTagFilter(
  filterKey: FilterKey,
  fieldName: string | null,
  value: string | null,
  fieldType: FieldType,
  op: Operator | null
): string {
  assert(
    // eslint-disable-next-line no-restricted-syntax
    value == null || _.isString(value) || _.isNumber(value),
    'Expected value to be null, string, or number'
  );
  return (
    `${op != null ? encodeValue(op) : defaultOpForFieldType(fieldType)}${FILTER_PART_SEPARATOR}` +
    `${value != null ? encodeValue(value) : ''}${FILTER_PART_SEPARATOR}` +
    `${encodeValue(fieldName)}${FILTER_PART_SEPARATOR}` +
    `${encodeValue(fieldType)}`
  );
}

// Multivalue fields have this representation in the search query string to preserve commas
// Null tag values are represented with the empty string (see `createTagFilter`)
export function tagFilterValueFromLinkItems(items: Array<{id: string}>): string {
  // eslint-disable-next-line no-restricted-syntax
  return tagFilterValueFromLinkIds(_.pluck(items, 'id'));
}

export function tagFilterValueFromLinkIds(ids: Array<string> | null): string {
  if (ids == null) {
    return '';
  }
  return ids.length ? JSON.stringify(ids) : '';
}

/**
 * These linkItems can be passed into ItemSearchInput components, as they only except objects with id;
 * all other information is fetched by the component's SmartChip.
 */
export function linkItemsFromTagFilterValue(value: string): Array<{id: string}> {
  return linkIdsFromTagFilterValue(value).map((id) => ({id}));
}

export function linkIdsFromTagFilterValue(value: string): Array<string> {
  if (!value) {
    return [];
  }
  try {
    // This is technically user-inputed data, as it comes from the URL, so we are defensive in parsing.
    return JSON.parse(value);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    return [];
  }
}

/**
 * Field types that cannot currently be used in the filter system, and thus do not have any
 * corresponding operators.
 */
export const NOT_FILTERABLE_FIELD_TYPES = [
  FieldType.ASSAY_REQUEST_LINK,
  FieldType.ASSAY_RESULT_LINK,
  FieldType.ASSAY_RUN_LINK,
  FieldType.JSON,
  FieldType.SAMPLE_LINK,
];

/**
 * Return an ordered list of operators supported by `fieldType`.
 * The first element of the array is the default operator for that fieldType.
 * Every field used in filtering supports at least one operator.
 *
 * @param {string} fieldType
 * @returns {Array}
 */
export function operatorsForFieldType(fieldType: FieldType): Array<Operator> {
  if (isTextFieldType(fieldType)) {
    return [Operator.INCLUDES_TEXT, Operator.EXCLUDES_TEXT, Operator.IS, Operator.IS_NOT];
  }
  if (isMultiValueFieldType(fieldType)) {
    return [
      Operator.HAS_ONE_OF,
      Operator.HAS_ALL_OF,
      Operator.HAS_NONE_OF,
      Operator.IS_EMPTY,
      Operator.IS_NOT_EMPTY,
    ];
  }
  if (COMPARABLE_FIELD_TYPES.includes(fieldType)) {
    return [Operator.EQ, Operator.LTE, Operator.GTE];
  }
  if (fieldType === FieldType.BOOLEAN) {
    return [Operator.IS_TRUE, Operator.IS_FALSE];
  }
  if (fieldType === FieldType.JSON) {
    return [];
  }
  throw new Error(`Unexpected fieldType ${fieldType}`);
}

// A copy of operatorsForFieldType that returns the standard filter interface set of operators
export function standardOperatorsForFieldType(fieldType: FieldType): Array<Operator> {
  if (isTextFieldType(fieldType)) {
    return standardOperatorsForKey(FilterKey.SCHEMA_FIELD, StandardFilterDataType.STRING);
  }
  if (isMultiValueFieldType(fieldType)) {
    return standardOperatorsForKey(FilterKey.SCHEMA_FIELD, StandardFilterDataType.MULTI_VALUE_ENTITY_LINK);
  }
  if (COMPARABLE_FIELD_TYPES.includes(fieldType)) {
    if (isNumericFieldType(fieldType)) {
      return standardOperatorsForKey(FilterKey.SCHEMA_FIELD, StandardFilterDataType.INT);
    } else {
      return standardOperatorsForKey(FilterKey.SCHEMA_FIELD, StandardFilterDataType.DATE);
    }
  }
  if (fieldType === FieldType.BOOLEAN) {
    return standardOperatorsForKey(FilterKey.SCHEMA_FIELD, StandardFilterDataType.BOOL);
  }
  if (fieldType === FieldType.JSON) {
    return [];
  }
  throw new Error(`Unexpected fieldType ${fieldType}`);
}

export function defaultOpForFieldType(fieldType: FieldType): Operator {
  const operators = operatorsForFieldType(fieldType);
  if (operators.length === 0) {
    throw new Error(`No operators for fieldType ${fieldType}`);
  }
  return operators[0];
}

export function defaultValueForFieldType(fieldType: FieldType): string | Array<string> | null {
  if (isStringFieldType(fieldType)) {
    return '';
  }
  if (isMultiValueFieldType(fieldType)) {
    return [];
  }
  if (COMPARABLE_FIELD_TYPES.includes(fieldType) || fieldType === FieldType.BOOLEAN) {
    return null;
  }
  throw new Error(`Unexpected fieldType ${fieldType}`);
}

export function emptyValuesForFieldType(fieldType: FieldType): Array<string | Array<string> | null> {
  const emptyValues = [defaultValueForFieldType(fieldType)];
  if (fieldType === FieldType.DATE || fieldType === FieldType.DATETIME) {
    emptyValues.push(null);
  }
  return emptyValues;
}

/**
 * Return a human-friendly name for the given filterKey.
 *
 * @param {string} filterKey
 * @param {object} nameOverrides Optional mapping of filterKey => override,
 *   to e.g. override "Name" with "Batch ID"
 */
export function getFilterName(
  filterKey: FilterKey,
  nameOverrides: {[filterKey in FilterKey]?: string} = {}
): string | null {
  const override = nameOverrides[filterKey];
  return override || FILTERS[filterKey].name || null;
}

interface SearchFilter<T extends Filter> {
  id: string;
  filter: T;
}

/**
 * Update searchFilters by splicing newSearchFilter in lieu of the old one by the same id
 *
 * A "searchFilter" is an object of {id, filter} - it stores a filter with a unique ID for components to use.
 * @param {Object[]} searchFilters The list of searchFilters to update
 * @param {Object} newSearchFilter The new filter to splice in
 */
export function spliceSearchFilters<T extends Filter>(
  searchFilters: Array<SearchFilter<T>>,
  newSearchFilter: SearchFilter<T>
): Array<SearchFilter<T>> {
  // eslint-disable-next-line no-restricted-syntax
  const index = _.findIndex(searchFilters, (searchFilter) => searchFilter.id === newSearchFilter.id);
  return reactUpdate(searchFilters, {
    $splice: [[index, 1, newSearchFilter]],
  });
}

export function getFiltersByKey<T extends Filter>(
  filterObjects: Array<T>,
  filterKey: FilterKey,
  excludeFiltersWithNullValue: boolean = true
): Array<T> {
  return filterObjects.filter(
    ({key, value}) => key === filterKey && (!excludeFiltersWithNullValue || value != null)
  );
}

export function getFilterByKey<T extends Filter>(filterObjects: Array<T>, filterKey: FilterKey): T | null {
  const filters = getFiltersByKey(filterObjects, filterKey);
  assert(filters.length <= 1, `Expected only one filter to exist for ${filterKey}`);
  return filters.length ? filters[0] : null;
}

export function getFilterValueByKey<T extends NewFilter>(
  filterObjects: Array<T>,
  filterKey: FilterKey
): T['value'] {
  const filter = getFilterByKey(filterObjects, filterKey);
  return filter ? filter.value : keyToEmptyFilter(filterKey).value;
}

export function getSelectedSchema(
  filters: Array<Filter>,
  schemas: Array<Schema | BatchSchema>
): Schema | BatchSchema | null {
  const filter =
    (getFilterByKey(filters, FilterKey.SCHEMA) as MultiValueFilter | null) ||
    (getFilterByKey(filters, FilterKey.ASSAY_SCHEMA) as SingleValueFilter | null) ||
    (getFilterByKey(filters, FilterKey.SCHEMA_INTERFACE) as MultiValueFilter | null);

  if (filter == null) {
    return null;
  }

  return (
    schemas.find((schema) => {
      if (isMultiValueFilter(filter)) {
        return filter.value.includes(schema.id);
      } else {
        return schema.id === filter.value;
      }
    }) || null
  );
}

/**
 * Returns a temporary copy of the specified search params that inclues modifications needed to fetch
 * the children of the specified group.
 *
 * @param {SearchParams} params The original search paramters to adjust
 * @param {Array<any>} groupByValues An array of group by keys for the groups we are expanding
 * @returns {SearchParams} a new SearchParams object altered for the expansion.
 */
export function getGroupChildrenSearchParamsAndErrors(
  params: SearchParams,
  groupByValues: Array<string>,
  filterErrors: Array<string | null>,
  offset: number,
  limit: number
): [SearchParams, Array<string | null>] {
  if (!params.groupBy) {
    throw new Error('Cannot expand a group for SearchParams where groupBy is not set.');
  }

  const groupByKeyFilter: GroupByKeyFilter = {
    key: FilterKey.GROUP_BY_KEY,
    operator: Operator.IS_ONE_OF,
    groupByField: params.groupBy.groupField,
    value: groupByValues,
  };
  return [
    {
      ...params,
      // Since we are filtering to a specific set of groups, results will only contain those groups so
      // we should ignore paging values. Even with an "expand all" feature, this will still be limited
      // to the current page size (since thats the max number of groups that we could include), so we
      // don't need to override limit.
      offset: 0,
      filters: params.filters.concat(groupByKeyFilter),
      groupBy: {
        ...params.groupBy,
        childOffset: offset,
        childLimit: limit,
      },
    },
    // Add a null filter error for the new filter being added.
    [...filterErrors, null],
  ];
}

/** Updates a filter value to replace the term procedure (if present) with its subtypes Recipe and Assay.
 *
 * Throws assertion error if the filter key is not 'types'.
 * @param filter a MultiValueFilter that potentially includes procedure as a value in the filter
 */
export function replaceProcedureTypeFilter(filter: MultiValueFilter): MultiValueFilter {
  assert(filter.key === FilterKey.TYPES, "This function should only be called with a filter of key 'types'");
  const procedureIndex = filter.value.indexOf(ModelTypeConstants.PROCEDURE);
  if (procedureIndex === -1) {
    return filter;
  }

  const value = [...filter.value];
  value.splice(
    procedureIndex,
    1,
    ...[ModelTypeConstants.RECIPE_PROCEDURE.toString(), ModelTypeConstants.ASSAY_PROCEDURE.toString()]
  );
  return {
    ...filter,
    value,
  };
}
